import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Trophy, MapPin, Calendar, Clock, Car, Hotel, 
  Plus, Trash2, ChevronRight, ChevronLeft, Check,
  AlertCircle, Sparkles, Heart, Users, Plane
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TOURNAMENT_TYPES = [
  { id: 'single_game', label: 'Single Game', icon: Trophy, description: 'One game, one day' },
  { id: 'multi_day', label: 'Multi-Day Tournament', icon: Calendar, description: 'Pool play + brackets over multiple days' },
  { id: 'league_game', label: 'League Game', icon: Users, description: 'Part of ongoing season schedule' },
  { id: 'showcase', label: 'Showcase Event', icon: Sparkles, description: 'Exposure event with scouts' },
];

const DAY_TYPES = [
  { id: 'travel', label: 'Travel Day', color: 'bg-blue-500' },
  { id: 'pool_play', label: 'Pool Play', color: 'bg-green-500' },
  { id: 'bracket_play', label: 'Bracket Play', color: 'bg-yellow-500' },
  { id: 'championship', label: 'Championship', color: 'bg-red-500' },
  { id: 'recovery', label: 'Recovery Day', color: 'bg-purple-500' },
];

const TournamentWizard = ({ open, onOpenChange, onTournamentCreated }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [tournamentData, setTournamentData] = useState({
    name: '',
    tournament_type: 'multi_day',
    sport: '',
    location: '',
    start_date: '',
    end_date: '',
    travel: {
      distance_miles: '',
      drive_time_hours: '',
      departure_datetime: '',
      return_datetime: '',
      overnight_stays: 0,
      hotel_name: '',
      needs_early_departure: false
    },
    days: [],
    games: [],
    recovery_appointments: [],
    other_commitments: [],
    notes: ''
  });

  const totalSteps = 5;

  const updateTournament = (field, value) => {
    setTournamentData(prev => ({ ...prev, [field]: value }));
  };

  const updateTravel = (field, value) => {
    setTournamentData(prev => ({
      ...prev,
      travel: { ...prev.travel, [field]: value }
    }));
  };

  // Day management
  const addDay = () => {
    const newDate = tournamentData.start_date || new Date().toISOString().split('T')[0];
    setTournamentData(prev => ({
      ...prev,
      days: [...prev.days, {
        date: newDate,
        day_type: 'pool_play',
        games_expected: 2,
        games_min: 2,
        games_max: 4,
        first_game_time: '09:00',
        last_game_time: '18:00',
        notes: ''
      }]
    }));
  };

  const updateDay = (index, field, value) => {
    setTournamentData(prev => ({
      ...prev,
      days: prev.days.map((day, i) => i === index ? { ...day, [field]: value } : day)
    }));
  };

  const removeDay = (index) => {
    setTournamentData(prev => ({
      ...prev,
      days: prev.days.filter((_, i) => i !== index)
    }));
  };

  // Game management
  const addGame = () => {
    setTournamentData(prev => ({
      ...prev,
      games: [...prev.games, {
        opponent: '',
        start_time: '',
        end_time: '',
        location: '',
        notes: ''
      }]
    }));
  };

  const updateGame = (index, field, value) => {
    setTournamentData(prev => ({
      ...prev,
      games: prev.games.map((game, i) => i === index ? { ...game, [field]: value } : game)
    }));
  };

  const removeGame = (index) => {
    setTournamentData(prev => ({
      ...prev,
      games: prev.games.filter((_, i) => i !== index)
    }));
  };

  // Recovery appointment management
  const addRecoveryAppointment = () => {
    setTournamentData(prev => ({
      ...prev,
      recovery_appointments: [...prev.recovery_appointments, {
        name: '',
        datetime: '',
        duration_minutes: 60,
        location: '',
        appointment_type: 'recovery'
      }]
    }));
  };

  const updateRecoveryAppointment = (index, field, value) => {
    setTournamentData(prev => ({
      ...prev,
      recovery_appointments: prev.recovery_appointments.map((appt, i) => 
        i === index ? { ...appt, [field]: value } : appt
      )
    }));
  };

  const removeRecoveryAppointment = (index) => {
    setTournamentData(prev => ({
      ...prev,
      recovery_appointments: prev.recovery_appointments.filter((_, i) => i !== index)
    }));
  };

  // Other commitment management
  const addOtherCommitment = () => {
    setTournamentData(prev => ({
      ...prev,
      other_commitments: [...prev.other_commitments, {
        title: '',
        datetime: '',
        end_datetime: '',
        commitment_type: 'other',
        location: '',
        priority: 'medium',
        notes: ''
      }]
    }));
  };

  const updateOtherCommitment = (index, field, value) => {
    setTournamentData(prev => ({
      ...prev,
      other_commitments: prev.other_commitments.map((commit, i) => 
        i === index ? { ...commit, [field]: value } : commit
      )
    }));
  };

  const removeOtherCommitment = (index) => {
    setTournamentData(prev => ({
      ...prev,
      other_commitments: prev.other_commitments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('aura_token');
      
      // Prepare data
      const payload = {
        ...tournamentData,
        travel: tournamentData.travel.needs_early_departure ? tournamentData.travel : null,
        days: tournamentData.days.length > 0 ? tournamentData.days : null
      };
      
      const response = await axios.post(`${API}/calendar-hub/tournaments/v2`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Tournament created with ${response.data.all_events?.length || 0} events!`);
      
      if (response.data.recommendations?.length > 0) {
        response.data.recommendations.forEach(rec => {
          if (rec.priority === 'high') {
            toast.info(rec.message, { duration: 5000 });
          }
        });
      }
      
      onTournamentCreated?.(response.data);
      onOpenChange(false);
      
      // Reset form
      setTournamentData({
        name: '', tournament_type: 'multi_day', sport: '', location: '',
        start_date: '', end_date: '',
        travel: { distance_miles: '', drive_time_hours: '', departure_datetime: '',
          return_datetime: '', overnight_stays: 0, hotel_name: '', needs_early_departure: false },
        days: [], games: [], recovery_appointments: [], other_commitments: [], notes: ''
      });
      setStep(1);
      
    } catch (error) {
      console.error('Failed to create tournament:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return tournamentData.name && tournamentData.location && tournamentData.start_date;
      case 2:
        return true; // Travel is optional
      case 3:
        return true; // Days are optional if games are specified
      case 4:
        return true; // Games are optional if days are specified
      case 5:
        return true; // Recovery and other commitments are optional
      default:
        return true;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-aura-surface border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-white uppercase flex items-center gap-2">
            <Trophy className="w-5 h-5 text-green-400" />
            Tournament Wizard 2.0
          </DialogTitle>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-2 pt-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-aura-primary text-white' :
                  'bg-white/10 text-aura-text-muted'
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 5 && <div className={`w-8 h-0.5 ${s < step ? 'bg-green-500' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>
          <p className="text-xs text-aura-text-muted">
            {step === 1 && 'Basic Info'}
            {step === 2 && 'Travel Details'}
            {step === 3 && 'Day Structure'}
            {step === 4 && 'Specific Games'}
            {step === 5 && 'Recovery & Conflicts'}
          </p>
        </DialogHeader>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            {/* Tournament Type Selection */}
            <div className="grid grid-cols-2 gap-2">
              {TOURNAMENT_TYPES.map((type) => (
                <Card 
                  key={type.id}
                  className={`cursor-pointer transition-all ${
                    tournamentData.tournament_type === type.id 
                      ? 'bg-aura-primary/20 border-aura-primary' 
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                  onClick={() => updateTournament('tournament_type', type.id)}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <type.icon className={`w-5 h-5 ${tournamentData.tournament_type === type.id ? 'text-aura-primary' : 'text-aura-text-muted'}`} />
                    <div>
                      <p className="text-sm font-medium text-white">{type.label}</p>
                      <p className="text-xs text-aura-text-muted">{type.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase text-aura-text-muted">Tournament Name *</Label>
              <Input
                value={tournamentData.name}
                onChange={(e) => updateTournament('name', e.target.value)}
                placeholder="e.g., Spring Showcase, State Championship"
                className="bg-aura-bg border-white/10 text-white"
                data-testid="tournament-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-aura-text-muted">Sport</Label>
                <Input
                  value={tournamentData.sport}
                  onChange={(e) => updateTournament('sport', e.target.value)}
                  placeholder="e.g., Basketball, Soccer"
                  className="bg-aura-bg border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-aura-text-muted">Location *</Label>
                <Input
                  value={tournamentData.location}
                  onChange={(e) => updateTournament('location', e.target.value)}
                  placeholder="e.g., City Sports Complex"
                  className="bg-aura-bg border-white/10 text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-aura-text-muted">Start Date *</Label>
                <Input
                  type="date"
                  value={tournamentData.start_date}
                  onChange={(e) => updateTournament('start_date', e.target.value)}
                  className="bg-aura-bg border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-aura-text-muted">End Date</Label>
                <Input
                  type="date"
                  value={tournamentData.end_date}
                  onChange={(e) => updateTournament('end_date', e.target.value)}
                  className="bg-aura-bg border-white/10 text-white"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Travel Details */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Car className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-400 font-medium">Travel Information</p>
                  <p className="text-xs text-blue-300/80 mt-1">
                    Add travel details for away tournaments. Aura will create prep and recovery windows around your travel.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
              <Checkbox
                id="early-departure"
                checked={tournamentData.travel.needs_early_departure}
                onCheckedChange={(checked) => updateTravel('needs_early_departure', checked)}
              />
              <Label htmlFor="early-departure" className="text-sm text-white cursor-pointer">
                Need to travel the day before (recommended for 2+ hour drives)
              </Label>
            </div>
            
            {tournamentData.travel.needs_early_departure && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Distance (miles)</Label>
                    <Input
                      type="number"
                      value={tournamentData.travel.distance_miles}
                      onChange={(e) => updateTravel('distance_miles', e.target.value)}
                      placeholder="e.g., 150"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Drive Time (hours)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={tournamentData.travel.drive_time_hours}
                      onChange={(e) => updateTravel('drive_time_hours', e.target.value)}
                      placeholder="e.g., 3"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Departure Date/Time</Label>
                    <Input
                      type="datetime-local"
                      value={tournamentData.travel.departure_datetime}
                      onChange={(e) => updateTravel('departure_datetime', e.target.value)}
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Return Date/Time</Label>
                    <Input
                      type="datetime-local"
                      value={tournamentData.travel.return_datetime}
                      onChange={(e) => updateTravel('return_datetime', e.target.value)}
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Overnight Stays</Label>
                    <Input
                      type="number"
                      value={tournamentData.travel.overnight_stays}
                      onChange={(e) => updateTravel('overnight_stays', parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Hotel Name</Label>
                    <Input
                      value={tournamentData.travel.hotel_name}
                      onChange={(e) => updateTravel('hotel_name', e.target.value)}
                      placeholder="e.g., Hampton Inn"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Day Structure */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <p className="text-sm text-green-400 font-medium">Day-by-Day Structure</p>
                  <p className="text-xs text-green-300/80 mt-1">
                    Define each tournament day type. Pool play days have multiple games back-to-back. 
                    Bracket days may have variable game counts based on results.
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={addDay}
              className="w-full border-dashed border-white/30 text-white hover:bg-white/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tournament Day
            </Button>
            
            {tournamentData.days.map((day, idx) => (
              <Card key={idx} className="bg-white/5 border-white/10">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white">Day {idx + 1}</span>
                    <Button 
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeDay(idx)}
                      className="text-red-400 hover:text-red-300 h-7 px-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-aura-text-muted">Date</Label>
                      <Input
                        type="date"
                        value={day.date}
                        onChange={(e) => updateDay(idx, 'date', e.target.value)}
                        className="bg-aura-bg border-white/10 text-white text-sm h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-aura-text-muted">Day Type</Label>
                      <select
                        value={day.day_type}
                        onChange={(e) => updateDay(idx, 'day_type', e.target.value)}
                        className="w-full h-9 px-3 rounded-md bg-aura-bg border border-white/10 text-white text-sm"
                      >
                        {DAY_TYPES.map(type => (
                          <option key={type.id} value={type.id}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {day.day_type !== 'travel' && day.day_type !== 'recovery' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-aura-text-muted">Min Games</Label>
                        <Input
                          type="number"
                          value={day.games_min}
                          onChange={(e) => updateDay(idx, 'games_min', parseInt(e.target.value) || 0)}
                          className="bg-aura-bg border-white/10 text-white text-sm h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-aura-text-muted">Max Games</Label>
                        <Input
                          type="number"
                          value={day.games_max}
                          onChange={(e) => updateDay(idx, 'games_max', parseInt(e.target.value) || 0)}
                          className="bg-aura-bg border-white/10 text-white text-sm h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-aura-text-muted">First Game</Label>
                        <Input
                          type="time"
                          value={day.first_game_time}
                          onChange={(e) => updateDay(idx, 'first_game_time', e.target.value)}
                          className="bg-aura-bg border-white/10 text-white text-sm h-9"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {tournamentData.days.length === 0 && (
              <p className="text-center text-aura-text-muted text-sm py-4">
                No days added yet. Add days to get tournament-specific prep protocols.
              </p>
            )}
          </div>
        )}

        {/* Step 4: Specific Games */}
        {step === 4 && (
          <div className="space-y-4 py-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Trophy className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-400 font-medium">Specific Games (Optional)</p>
                  <p className="text-xs text-yellow-300/80 mt-1">
                    If you know your game schedule, add specific games here. 
                    Aura will create prep and recovery windows for each.
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={addGame}
              className="w-full border-dashed border-white/30 text-white hover:bg-white/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Game
            </Button>
            
            {tournamentData.games.map((game, idx) => (
              <Card key={idx} className="bg-white/5 border-white/10">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white">Game {idx + 1}</span>
                    <Button 
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeGame(idx)}
                      className="text-red-400 hover:text-red-300 h-7 px-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Input
                    placeholder="Opponent (e.g., Eagles)"
                    value={game.opponent}
                    onChange={(e) => updateGame(idx, 'opponent', e.target.value)}
                    className="bg-aura-bg border-white/10 text-white"
                  />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-aura-text-muted">Game Time</Label>
                      <Input
                        type="datetime-local"
                        value={game.start_time}
                        onChange={(e) => updateGame(idx, 'start_time', e.target.value)}
                        className="bg-aura-bg border-white/10 text-white text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-aura-text-muted">End Time</Label>
                      <Input
                        type="datetime-local"
                        value={game.end_time}
                        onChange={(e) => updateGame(idx, 'end_time', e.target.value)}
                        className="bg-aura-bg border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {tournamentData.games.length === 0 && (
              <p className="text-center text-aura-text-muted text-sm py-4">
                No specific games added. You can add them later or use Day Structure instead.
              </p>
            )}
          </div>
        )}

        {/* Step 5: Recovery & Other Commitments */}
        {step === 5 && (
          <div className="space-y-4 py-4">
            <Tabs defaultValue="recovery" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white/5">
                <TabsTrigger value="recovery" className="data-[state=active]:bg-purple-500/20">
                  <Heart className="w-4 h-4 mr-2" />
                  Recovery Appts
                </TabsTrigger>
                <TabsTrigger value="conflicts" className="data-[state=active]:bg-orange-500/20">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Other Commitments
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="recovery" className="space-y-4 mt-4">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                  <p className="text-xs text-purple-300/80">
                    Add recovery appointments (massage, cryo, Restore Wellness, etc.) 
                    to integrate with your tournament schedule.
                  </p>
                </div>
                
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={addRecoveryAppointment}
                  className="w-full border-dashed border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Recovery Appointment
                </Button>
                
                {tournamentData.recovery_appointments.map((appt, idx) => (
                  <Card key={idx} className="bg-purple-500/10 border-purple-500/30">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-300">Recovery {idx + 1}</span>
                        <Button 
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRecoveryAppointment(idx)}
                          className="text-red-400 hover:text-red-300 h-7 px-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <Input
                        placeholder="Name (e.g., Restore Wellness, Sports Massage)"
                        value={appt.name}
                        onChange={(e) => updateRecoveryAppointment(idx, 'name', e.target.value)}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-aura-text-muted">Date/Time</Label>
                          <Input
                            type="datetime-local"
                            value={appt.datetime}
                            onChange={(e) => updateRecoveryAppointment(idx, 'datetime', e.target.value)}
                            className="bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-aura-text-muted">Duration (min)</Label>
                          <Input
                            type="number"
                            value={appt.duration_minutes}
                            onChange={(e) => updateRecoveryAppointment(idx, 'duration_minutes', parseInt(e.target.value) || 60)}
                            className="bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
              
              <TabsContent value="conflicts" className="space-y-4 mt-4">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-xs text-orange-300/80">
                    Have other commitments during the tournament (local team games, school events)? 
                    Add them here so Aura can plan around conflicts.
                  </p>
                </div>
                
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={addOtherCommitment}
                  className="w-full border-dashed border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Other Commitment
                </Button>
                
                {tournamentData.other_commitments.map((commit, idx) => (
                  <Card key={idx} className="bg-orange-500/10 border-orange-500/30">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-orange-300">Commitment {idx + 1}</span>
                        <Button 
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeOtherCommitment(idx)}
                          className="text-red-400 hover:text-red-300 h-7 px-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <Input
                        placeholder="Title (e.g., Local Team Game, SAT Test)"
                        value={commit.title}
                        onChange={(e) => updateOtherCommitment(idx, 'title', e.target.value)}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-aura-text-muted">Start Time</Label>
                          <Input
                            type="datetime-local"
                            value={commit.datetime}
                            onChange={(e) => updateOtherCommitment(idx, 'datetime', e.target.value)}
                            className="bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-aura-text-muted">Type</Label>
                          <select
                            value={commit.commitment_type}
                            onChange={(e) => updateOtherCommitment(idx, 'commitment_type', e.target.value)}
                            className="w-full h-9 px-3 rounded-md bg-aura-bg border border-white/10 text-white text-sm"
                          >
                            <option value="local_team_game">Local Team Game</option>
                            <option value="school">School</option>
                            <option value="family">Family</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2 pt-4 border-t border-white/10">
          <div>
            {step > 1 && (
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setStep(step - 1)}
                className="text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-white/20 text-white"
            >
              Cancel
            </Button>
            {step < totalSteps ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-aura-primary"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className="bg-green-500 hover:bg-green-600"
              >
                {isSubmitting ? 'Creating...' : 'Create Tournament'}
                <Check className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TournamentWizard;
